import React, { useState, useEffect } from 'react';
import { Package, Clock, CheckCircle, XCircle, Eye } from 'lucide-react';
import { useRedemptions } from '../../hooks/useRewards';
import { useAuth } from '../../contexts/AuthContext';
import { customerService } from '../../services/customerService';
import { getRewardBatches } from '../../services/rewardsService';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';

const RedemptionsManager = () => {
  const { user } = useAuth();
  const { 
    redemptions, 
    loading, 
    error, 
    fetchCompanyRedemptions, 
    updateStatus,
    fetchRedemptionDetails 
  } = useRedemptions();

  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRedemption, setSelectedRedemption] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({});
  const [rewardDetails, setRewardDetails] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [loadingBatches, setLoadingBatches] = useState(false);

  useEffect(() => {
    if (user?.companyId) {
      loadRedemptions();
    }
  }, [user?.companyId, selectedStatus]);

  const loadRedemptions = async () => {
    const statusFilter = selectedStatus === 'all' ? null : selectedStatus;
    await fetchCompanyRedemptions(user.companyId, statusFilter);
  };

  const loadCustomerDetails = async (customerId, forceReload = false) => {
    if (!forceReload && customerDetails[customerId]) return customerDetails[customerId];
    
    try {
      const customer = await customerService.getById(customerId);
      setCustomerDetails(prev => ({
        ...prev,
        [customerId]: customer
      }));
      return customer;
    } catch (error) {
      console.error('Erro ao carregar dados do cliente:', error);
      return null;
    }
  };

  const loadRewardDetails = async (rewardId, forceReload = false) => {
    if (!rewardId) return null;
    if (!forceReload && rewardDetails[rewardId]) return rewardDetails[rewardId];
    
    try {
      const { getRewardById } = await import('../../services/rewardsService');
      const reward = await getRewardById(rewardId);
      setRewardDetails(prev => ({
        ...prev,
        [rewardId]: reward
      }));
      return reward;
    } catch (error) {
      console.error('Erro ao carregar dados do prêmio:', error);
      return null;
    }
  };

  const handleViewDetails = async (redemption) => {
    setSelectedRedemption(redemption);
    await loadCustomerDetails(redemption.customerId);
    
    // Carregar dados completos do prêmio se disponível
    if (redemption.rewardId) {
      await loadRewardDetails(redemption.rewardId);
    }
    
    // Carregar lotes disponíveis se for produto/aluguel com controle de estoque
    if (redemption.rewardId && redemption.hasStockControl !== false && 
        (redemption.rewardType === 'product' || redemption.rewardType === 'rental')) {
      await loadAvailableBatches(redemption.rewardId);
    }
    
    setShowModal(true);
  };

  const loadAvailableBatches = async (rewardId) => {
    setLoadingBatches(true);
    try {
      const batches = await getRewardBatches(rewardId);
      setAvailableBatches(batches);
      
      // Selecionar o primeiro lote disponível automaticamente
      if (batches.length > 0) {
        setSelectedBatch(batches[0].batchCode);
      }
    } catch (error) {
      console.error('Erro ao carregar lotes:', error);
      setAvailableBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleStatusChange = async (redemptionId, newStatus) => {
    try {
      // Validar seleção de lote para aprovação de produtos/aluguéis com controle de estoque
      if (newStatus === 'approved' && 
          selectedRedemption.rewardId && 
          selectedRedemption.hasStockControl !== false && 
          (selectedRedemption.rewardType === 'product' || selectedRedemption.rewardType === 'rental')) {
        
        if (availableBatches.length > 0 && !selectedBatch) {
          alert('Por favor, selecione um lote antes de aprovar o resgate.');
          return;
        }
      }

      // Confirmar ação crítica (completar resgate)
      if (newStatus === 'completed') {
        const customer = customerDetails[selectedRedemption.customerId];
        const confirmed = window.confirm(
          `Tem certeza que deseja marcar este resgate como entregue?\n\n` +
          `Isso irá descontar ${selectedRedemption.pointsUsed} pontos do cliente ${customer?.name || 'desconhecido'}.\n\n` +
          `Esta ação NÃO pode ser desfeita.`
        );
        
        if (!confirmed) {
          return;
        }
      }

      // Incluir informação do lote selecionado se disponível
      const additionalData = {};
      if (selectedBatch && availableBatches.length > 0) {
        additionalData.selectedBatchCode = selectedBatch;
      }

      await updateStatus(redemptionId, newStatus, notes, additionalData);
      
      // Recarregar dados do cliente se o resgate foi completado
      if (newStatus === 'completed' && selectedRedemption.customerId) {
        await loadCustomerDetails(selectedRedemption.customerId, true);
      }
      
      // Mostrar mensagem de sucesso específica
      if (newStatus === 'completed') {
        alert(`Resgate processado com sucesso! ${selectedRedemption.pointsUsed} pontos foram descontados do cliente.`);
      } else if (newStatus === 'approved') {
        alert('Resgate aprovado! O cliente pode procurar a empresa para retirar o prêmio.');
      } else if (newStatus === 'rejected') {
        alert('Resgate rejeitado. O cliente será notificado.');
      }
      
      setNotes('');
      setSelectedBatch('');
      setAvailableBatches([]);
      setShowModal(false);
      setSelectedRedemption(null);
      await loadRedemptions();
    } catch (error) {
      alert('Erro ao atualizar status: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      approved: 'Aprovado',
      completed: 'Concluído',
      rejected: 'Rejeitado'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} />;
      case 'approved':
        return <CheckCircle size={16} className="text-blue-600" />;
      case 'completed':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'rejected':
        return <XCircle size={16} className="text-red-600" />;
      default:
        return <Clock size={16} />;
    }
  };

  if (loading && redemptions.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Package className="mr-2" />
            Gerenciar Resgates
          </h1>
          <p className="text-gray-600 mt-1">
            Aprove, rejeite ou finalize os resgates solicitados pelos clientes
          </p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Todos os Status</option>
            <option value="pending">Pendentes</option>
            <option value="approved">Aprovados</option>
            <option value="completed">Concluídos</option>
            <option value="rejected">Rejeitados</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        {redemptions.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum resgate encontrado
            </h3>
            <p className="text-gray-600">
              {selectedStatus === 'all' 
                ? 'Ainda não há solicitações de resgate'
                : `Nenhum resgate com status "${getStatusLabel(selectedStatus)}"`
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente / Prêmio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pontos Usados
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lote
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Solicitação
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {redemptions.map((redemption) => (
                  <tr key={redemption.id}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Cliente ID: {redemption.customerId.substring(0, 8)}...
                        </div>
                        <div className="text-sm text-gray-600">
                          {redemption.rewardName}
                          {redemption.type === 'manual' && (
                            <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                              Manual
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {redemption.pointsUsed} pontos
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {redemption.selectedBatchCode ? (
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-mono">
                          {redemption.selectedBatchCode}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(redemption.status)}`}>
                        {getStatusIcon(redemption.status)}
                        {getStatusLabel(redemption.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {redemption.requestedAt?.toDate?.()?.toLocaleDateString('pt-BR') || 'Data não disponível'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleViewDetails(redemption)}
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                      >
                        <Eye size={16} />
                        Ver Detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Resgate */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedRedemption(null);
          setNotes('');
          setSelectedBatch('');
          setAvailableBatches([]);
          setRewardDetails({});
        }}
        title={`Detalhes do Resgate${selectedRedemption?.type === 'manual' ? ' (Manual/Forçado)' : ''}`}
      >
        {selectedRedemption && (
          <div className="space-y-6">
            {/* Alerta para Resgates Manuais */}
            {selectedRedemption.type === 'manual' && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-purple-800">
                      Resgate Manual/Forçado
                    </h3>
                    <div className="mt-1 text-sm text-purple-700">
                      <p>Este resgate foi processado diretamente pela empresa através da funcionalidade "Forçar Resgate". Os pontos já foram descontados automaticamente do cliente.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Informações do Cliente */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Cliente</h3>
              {customerDetails[selectedRedemption.customerId] ? (
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Nome:</span> {customerDetails[selectedRedemption.customerId].name}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">CPF:</span> {customerDetails[selectedRedemption.customerId].cpf}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Telefone:</span> {customerDetails[selectedRedemption.customerId].phone}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Pontos Totais:</span> {customerDetails[selectedRedemption.customerId].points || 0}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Carregando dados do cliente...</p>
              )}
            </div>

            {/* Informações do Prêmio */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Prêmio Solicitado</h3>
              <p className="text-sm mb-2">
                <span className="font-medium">Prêmio:</span> {selectedRedemption.rewardName}
              </p>
              <p className="text-sm mb-2">
                <span className="font-medium">Pontos Necessários:</span> {selectedRedemption.pointsUsed} pontos
              </p>
              
              {/* Mostrar lote original do prêmio se disponível */}
              {selectedRedemption.rewardId && rewardDetails[selectedRedemption.rewardId]?.batchCode && (
                <div className="bg-white border border-blue-200 rounded p-2 mb-2">
                  <p className="text-sm">
                    <span className="font-medium text-blue-700">Lote do Prêmio:</span> 
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs ml-2 font-mono">
                      {rewardDetails[selectedRedemption.rewardId].batchCode}
                    </span>
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    ℹ️ Lote cadastrado no estoque do prêmio
                  </p>
                </div>
              )}
              
              {/* Mostrar lote selecionado para aprovação/entrega */}
              {selectedRedemption.selectedBatchCode && (
                <div className="bg-white border border-blue-200 rounded p-2 mb-2">
                  <p className="text-sm">
                    <span className="font-medium text-blue-700">Lote Aprovado:</span> 
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs ml-2 font-mono font-semibold">
                      {selectedRedemption.selectedBatchCode}
                    </span>
                  </p>
                  {selectedRedemption.status === 'approved' && (
                    <p className="text-xs text-blue-600 mt-1">
                      ℹ️ Este lote será debitado do estoque ao confirmar a entrega
                    </p>
                  )}
                  {selectedRedemption.status === 'completed' && (
                    <p className="text-xs text-green-600 mt-1">
                      ✅ Lote já debitado do estoque
                    </p>
                  )}
                </div>
              )}
              {customerDetails[selectedRedemption.customerId] && (
                <p className="text-sm">
                  <span className="font-medium">Pontos do Cliente Após Resgate:</span> {' '}
                  <span className={`font-semibold ${
                    (customerDetails[selectedRedemption.customerId].points || 0) - selectedRedemption.pointsUsed >= 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {(customerDetails[selectedRedemption.customerId].points || 0) - selectedRedemption.pointsUsed} pontos
                  </span>
                </p>
              )}
            </div>

            {/* Status Atual */}
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
              <span className="text-yellow-800">Status Atual:</span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedRedemption.status)}`}>
                {getStatusIcon(selectedRedemption.status)}
                {getStatusLabel(selectedRedemption.status)}
              </span>
            </div>

            {/* Informações sobre o processo */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Processo de Resgate:</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Pendente:</strong> Aguardando aprovação da empresa</p>
                <p><strong>Aprovado:</strong> Cliente pode procurar a empresa para retirar o prêmio</p>
                <p><strong>Concluído:</strong> Prêmio entregue e pontos descontados automaticamente</p>
                <p><strong>Rejeitado:</strong> Solicitação negada pela empresa</p>
              </div>
              {selectedRedemption.status === 'approved' && (
                <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-200">
                  <p className="text-sm text-blue-800">
                    ⚠️ <strong>Importante:</strong> Só marque como "Concluído" após entregar fisicamente o prêmio ao cliente. 
                    Os pontos serão descontados automaticamente e esta ação não pode ser desfeita.
                  </p>
                </div>
              )}
            </div>

            {/* Data da Solicitação */}
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Solicitado em:</span> {' '}
                {selectedRedemption.requestedAt?.toDate?.()?.toLocaleString('pt-BR') || 'Data não disponível'}
              </p>
              {selectedRedemption.approvedAt && (
                <p>
                  <span className="font-medium">Aprovado em:</span> {' '}
                  {selectedRedemption.approvedAt?.toDate?.()?.toLocaleString('pt-BR')}
                </p>
              )}
              {selectedRedemption.completedAt && (
                <p>
                  <span className="font-medium">Concluído em:</span> {' '}
                  {selectedRedemption.completedAt?.toDate?.()?.toLocaleString('pt-BR')}
                </p>
              )}
              {selectedRedemption.rejectedAt && (
                <p>
                  <span className="font-medium">Rejeitado em:</span> {' '}
                  {selectedRedemption.rejectedAt?.toDate?.()?.toLocaleString('pt-BR')}
                </p>
              )}
            </div>

            {/* Notas */}
            {selectedRedemption.status === 'pending' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações (opcional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Adicione observações sobre este resgate..."
                />
              </div>
            )}

            {/* Seleção de Lote */}
            {selectedRedemption.status === 'pending' && 
             selectedRedemption.rewardId && 
             selectedRedemption.hasStockControl !== false && 
             (selectedRedemption.rewardType === 'product' || selectedRedemption.rewardType === 'rental') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lote para débito {availableBatches.length > 0 ? '*' : ''}
                </label>
                {loadingBatches ? (
                  <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-600">Carregando lotes...</span>
                  </div>
                ) : availableBatches.length > 0 ? (
                  <select
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Selecione um lote</option>
                    {availableBatches.map((batch) => (
                      <option key={batch.batchCode} value={batch.batchCode}>
                        {batch.batchCode} (Estoque: {batch.stockQuantity})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">
                      ⚠️ Nenhum lote disponível para este produto. Verifique o estoque.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Notas existentes */}
            {selectedRedemption.notes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-1">Observações:</h4>
                <p className="text-sm text-gray-600">{selectedRedemption.notes}</p>
              </div>
            )}

            {/* Ações */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedRedemption(null);
                  setNotes('');
                  setSelectedBatch('');
                  setAvailableBatches([]);
                  setRewardDetails({});
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Fechar
              </button>

              {/* Não mostrar ações para resgates manuais que já estão concluídos */}
              {!(selectedRedemption.type === 'manual' && selectedRedemption.status === 'completed') && (
                <>
                  {selectedRedemption.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleStatusChange(selectedRedemption.id, 'rejected')}
                        disabled={loading}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        Rejeitar
                      </button>
                      <button
                        onClick={() => handleStatusChange(selectedRedemption.id, 'approved')}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        Aprovar
                      </button>
                    </>
                  )}

                  {selectedRedemption.status === 'approved' && (
                    <div className="flex flex-col gap-2">
                      {/* Mostrar informações do lote se disponível */}
                      {selectedRedemption.selectedBatchCode && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-800">
                            <span className="font-medium">Lote a ser debitado:</span> 
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs ml-2 font-mono">
                              {selectedRedemption.selectedBatchCode}
                            </span>
                          </p>
                        </div>
                      )}
                      <button
                        onClick={() => handleStatusChange(selectedRedemption.id, 'completed')}
                        disabled={loading}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        <CheckCircle size={16} />
                        Confirmar Entrega ({selectedRedemption.pointsUsed} pts serão descontados)
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RedemptionsManager;
